import React, {Component} from "react";
import Nprogress from "nprogress";
import ReactPlaceholder from "react-placeholder";
import "nprogress/nprogress.css";

import "react-placeholder/lib/reactPlaceholder.css";
import CircularProgress from "components/CircularProgress";

// Helper function to detect if response is HTML instead of JavaScript
const isHTMLResponse = (response) => {
  const contentType = response.headers?.get('content-type') || '';
  return contentType.includes('text/html') || contentType.includes('application/json');
};

// Helper function to retry chunk loading with exponential backoff
const retryChunkLoad = (importComponent, retries = 3, delay = 1000) => {
  return new Promise((resolve, reject) => {
    const attemptLoad = (attempt) => {
      importComponent()
        .then((module) => {
          // Verify that we got a valid module, not HTML
          if (module && typeof module === 'object' && module.default) {
            resolve(module);
          } else {
            throw new Error('Invalid module response - received HTML instead of JavaScript');
          }
        })
        .catch((error) => {
          // Check if it's a chunk loading error
          const isChunkError = error.message && (
            error.message.includes('Loading chunk') ||
            error.message.includes('ChunkLoadError') ||
            error.message.includes('Unexpected token') ||
            error.message.includes('Invalid module response') ||
            error.name === 'ChunkLoadError' ||
            error.name === 'SyntaxError'
          );

          if (isChunkError) {
            console.warn(`[ChunkLoadError] Attempt ${attempt + 1}/${retries}:`, error.message);
            
            // Clear cache on first retry
            if (attempt === 0 && 'caches' in window) {
              caches.keys().then(names => {
                names.forEach(name => caches.delete(name));
              });
            }
            
            // Force reload if it's the last retry
            if (attempt >= retries - 1) {
              console.warn('[ChunkLoadError] All retries failed, reloading page...');
              setTimeout(() => {
                // Remove existing ?v= parameters to prevent multiple ?v= in URL
                const currentUrl = window.location.href.split('#')[0];
                const urlWithoutParams = currentUrl.split('?')[0];
                window.location.href = urlWithoutParams + '?v=' + Date.now();
              }, 500);
              return;
            }
            
            // Exponential backoff
            setTimeout(() => attemptLoad(attempt + 1), delay * Math.pow(2, attempt));
          } else {
            reject(error);
          }
        });
    };
    attemptLoad(0);
  });
};

export default function asyncComponent(importComponent) {
  class AsyncFunc extends Component {
    constructor(props) {
      super(props);
      this.state = {
        component: null,
        error: null,
        retrying: false
      };
    }

    UNSAFE_componentWillMount() {
      Nprogress.start();
    }

    componentWillUnmount() {
      this.mounted = false;
    }

    async componentDidMount() {
      this.mounted = true;
      try {
        const {default: Component} = await retryChunkLoad(importComponent);
        Nprogress.done();
        if (this.mounted) {
          this.setState({
            component: <Component {...this.props} />,
            error: null,
            retrying: false
          });
        }
      } catch (error) {
        console.error('[AsyncComponent] Failed to load component:', error);
      Nprogress.done();
      if (this.mounted) {
        this.setState({
            error: error,
            retrying: false
        });
        }
      }
    }

    render() {
      const { component, error } = this.state;
      
      if (error) {
        return (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <p style={{ color: '#ff4d4f', marginBottom: '16px' }}>
              Failed to load component. Please refresh the page.
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '8px 16px',
                backgroundColor: '#1890ff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Refresh Page
            </button>
          </div>
        );
      }

      const Component = component || <CircularProgress/>;
      return (
        <ReactPlaceholder type="text" rows={7} ready={component !== null}>
          {Component}
        </ReactPlaceholder>
      );
    }
  }

  return AsyncFunc;
}